import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries } from 'react-query';
import { Main as Container } from 'Components/Main';
import { SectionBanner } from 'Components/common';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';
import { UsageBar } from 'Components/UsageBar';
import {
  Chip,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { deleteCompany, getPartnerDashboard } from 'services/company-service';
import { getPolicyMetrics } from 'services/policies-service';
import { getGaMetrics } from 'services/gap-analysis';
import { getUserMetrics } from 'services/users-service';
import { getCookieMetrics } from 'services/cookies-service';
import { useAuthUser, useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import { hasModulePermission } from 'utils/permission_utils';
import { Button, Dialog, PageHeader, EditSvg } from 'Components/ui';


export const CompaniesPartner = () => {
  const { t } = useTranslation('company');
  const navigate = useNavigate();
  const user = useSelector(useAuthUser);
  const selectedTenant = useSelector(useAuthTenant);

  const [reFetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [page, setPage] = useState(1);

  const {
    isLoading,
    error,
    data: dashboard,
  } = useQuery(['getPartnerDashboard', reFetch, page, selectedTenant?._id], async () => {
    const result = await getPartnerDashboard(page, 15, selectedTenant?._id);
    return result?.data ?? { data: [], page, totalPages: 0, summary: null };
  });

  useEffect(() => {
    if (error && error.response?.status === 403) {
      navigate('/no-permission');
    }
  }, [error, navigate]);

  const tenants = dashboard?.data ?? [];

  const metricsQueries = useQueries(
    tenants.map(tenant => ({
      queryKey: ['partner-metrics', tenant.slug],
      queryFn: () =>
        Promise.all([
          getPolicyMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalPolicies: null })),
          getGaMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalFrameworks: null })),
          getUserMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalUsers: null })),
          getCookieMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalDisclaimers: null })),
        ]),
      staleTime: 5 * 60 * 1000,
    }))
  );

  const getMetrics = (idx) => {
    const q = metricsQueries[idx];
    if (!q?.data) return { policy: null, ga: null, users: null, cookies: null };
    const [policy, ga, users, cookies] = q.data;
    return { policy, ga, users, cookies };
  };

  const hideDelete = () => !hasModulePermission(user.permissions, ['super_admin_global', 'admin_global']);

  const onDelete = item => { setItemToDelete(item); setDeleteOpen(true); };
  const onDeleteClose = () => { setItemToDelete(null); setDeleteOpen(false); };

  const onDeleteConfirm = async () => {
    try {
      const result = await deleteCompany(itemToDelete?._id);
      if (result.status === 204) {
        onDeleteClose();
        setRefetch(!reFetch);
      } else {
        throw new Error('Ocorreu um erro ao tentar remover a Empresa!');
      }
    } catch (error) {
      Dialog.alert({ title: 'Erro!', message: error.response?.data?.message ?? error.message, icon: 'error' });
    }
  };

  return (
    <Container>
      <PageHeader
        title={t('company:companiesPartner.title')}
        segments={[
          { label: t('common:breadcrumbs.generalSettings') },
          { label: t('company:companiesPartner.title') },
        ]}
      />
      <SectionBanner
        title={t('company:companiesPartner.banner.title')}
        description={t('company:companiesPartner.banner.description')}
      />

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2, mb: 1 }}>
        <Button variant="primary" startIcon={<AddIcon />} onClick={() => navigate('/companies-partner/add')}>
          {t('company:companiesPartner.addButton')}
        </Button>
      </Stack>

      {isLoading && <PageLoaderList />}
      {error && <Typography color="error">Erro ao carregar dados.</Typography>}

      {!isLoading && !error && tenants.length === 0 && (
        <NoData description="Não há Clientes para exibir" />
      )}

      {!isLoading && !error && tenants.length > 0 && (
        <>
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Plano</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Políticas</TableCell>
                <TableCell>Gap Analysis</TableCell>
                <TableCell>Usuários</TableCell>
                <TableCell>Cookies</TableCell>
                {!hideDelete() && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((tenant, idx) => {
                const { policy, ga, users, cookies } = getMetrics(idx);
                const limits = tenant.planLimits;
                return (
                  <TableRow key={tenant._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{tenant.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{tenant.slug}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={tenant.signedPlan ?? '—'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tenant.active ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={tenant.active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <UsageBar current={policy?.totalPolicies ?? 0} limit={limits?.maxPoliciesLimit} />
                    </TableCell>
                    <TableCell>
                      <UsageBar current={ga?.totalFrameworks ?? 0} limit={limits?.maxGapAnalysisLimit} />
                    </TableCell>
                    <TableCell>
                      <UsageBar current={users?.totalUsers ?? 0} limit={limits?.maxUsersLimit} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {cookies?.totalDisclaimers ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button iconOnly variant="icon" title={t('common:tooltips.edit')} onClick={() => navigate(`/companies-partner/edit/${tenant._id}`)}>
                        <EditSvg />
                      </Button>
                      {!hideDelete() && (
                        <Button iconOnly variant="icon" danger title={t('common:tooltips.delete')} onClick={() => onDelete(tenant)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Stack sx={{ justifyContent: 'center', flexDirection: 'row', mt: 2 }}>
            <Pagination
              count={dashboard.totalPages}
              onChange={(_, p) => setPage(p)}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </Stack>
        </>
      )}

      <Dialog
        open={deleteOpen}
        onConfirm={onDeleteConfirm}
        onCancel={onDeleteClose}
        message={`Tem certeza que deseja excluir ${itemToDelete?.name} como cliente?`}
      />

    </Container>
  );
};
